import { useCallback, useEffect } from "react";

import type { UserType } from "@/types/user";

import { getGoogleOAuthTokens } from "@/api/auth";
import env from "@/valid-env";
import { useMutation } from "@tanstack/react-query";

function getGoogleOAuthURL(userType: UserType) {
	const rootUrl = "https://accounts.google.com/o/oauth2/v2/auth";

	const options = {
		redirect_uri: location.origin + location.pathname,
		client_id: env.VITE_GOOGLE_CLIENT_ID,
		access_type: "offline",
		response_type: "code",
		prompt: "consent",
		state: userType,
		scope: [
			"https://www.googleapis.com/auth/userinfo.profile",
			"https://www.googleapis.com/auth/userinfo.email"
		].join(" ")
	};

	const qs = new URLSearchParams(options);
	return `${rootUrl}?${qs.toString()}`;
}

export interface GoogleOAuthTokens {
	id_token: string;
	access_token: string;
	expires_in: number;
	refresh_token: string;
	scope: string;
	token_type: string;
}
export function useGoogleOAuthSignIn() {
	const code = new URLSearchParams(window.location.search).get("code") ?? "";

	const mutation = useMutation({
		mutationFn: async ({ code }: { code: string }) => await getGoogleOAuthTokens({ code, redirect_uri: location.origin + location.pathname })
	});

	useEffect(() => {
		if (code) {
			const url = new URL(window.location.href);
			url.searchParams.delete("code");
			window.history.replaceState({}, document.title, url.toString());

			mutation.mutateAsync({ code }).catch((error) => {
				console.error("Error fetching Google OAuth tokens:", error);
				console.error("Error response:", error.response?.data);
			});
		}
	}, [code, mutation]);

	const oauthSignIn = useCallback((userType: UserType = "BUYER") => {
		window.location.href = getGoogleOAuthURL(userType);
	}, []);

	return {
		signIn: oauthSignIn,
		...mutation
	};
}
